import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Button, Modal, Upload} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {ExclamationCircleOutlined, InboxOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

import {config} from 'config/config';
import {ComponentAccessCheck, Select} from 'ui';
import generalHelper from 'utils/generalHelpers';
import useQueryApiClient from 'utils/useApiClient';

import {UserManualType, UserManualLastChangeType} from '../types';
import apiClient from '../../../../utils/apiClient';

interface UserManualLastChangesInterface {
    manualLastChanges: UserManualLastChangeType[];
}

const UserManualLastChanges: React.FC<UserManualLastChangesInterface> = ({manualLastChanges}) => (
    <div className='user-manual-last-changes'>
        <FormattedMessage id='general.last_changed' />:
        {!manualLastChanges.length && <span>-</span>}
        {manualLastChanges.map((change) => (
            <div className='user-manual-change-record' key={`${change.updatedAt}-${change.userId ?? 'x'}`}>
                {change.userId && (
                    <span>
            {change.name} {change.surname}
          </span>
                )}
                <span>{dayjs(change.updatedAt).format('DD-MM-YYYY')}</span>
            </div>
        ))}
    </div>
);

interface UserManualUploadInterface {
    type: UserManualType;
    language: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;
    setIsUploadLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isUploaded: boolean;
    refetchUserManual: (v: any) => void;
}

const UserManualUpload: React.FC<UserManualUploadInterface> = ({
    type,
    language,
    setLanguage,
    setIsUploadLoading,
    isUploaded,
    refetchUserManual,
}) => {
    const intl = useIntl();
    const {confirm} = Modal;
    const {Dragger} = Upload;

    const [languageList, setLanguageList] = useState<Record<string, string>>({});

    useQueryApiClient({
        request: {
            url: '/api/v1/language',
            method: 'GET',
        },
        onSuccess: (response) => {
            const languages = response?.languages;
            const options: Record<string, string> = {};
            Object.values(languages).forEach((value: any) => {
                options[value.code] = value.name;
            });

            setLanguageList(options);
        },
    });

    const {data: manualLastChanges, appendData: getManualLastChanges} = useQueryApiClient({
        request: {
            url: '/api/v1/courses/user-manual/last-changes',
            method: 'GET',
            disableOnMount: true,
        },
        onFinally: () => setIsUploadLoading(false),
    });

    const {appendData: uploadManual} = useQueryApiClient({
        request: {
            url: '/api/v1/courses/user-manual',
            method: 'POST',
            multipart: true,
        },
        onSuccess: () => {
            getManualLastChanges({type, language});
            refetchUserManual({type, language});
        },
        onError: () => setIsUploadLoading(false),
    });

    const {appendData: deleteManual} = useQueryApiClient({
        request: {
            url: '/api/v1/courses/user-manual',
            method: 'DELETE',
        },
        onSuccess: () => {
            getManualLastChanges({type, language});
            refetchUserManual({type, language});
        },
        onError: () => setIsUploadLoading(false),
    });

    const handleUpload = (file: string | Blob | RcFile) => {
        const bodyFormData = new FormData();
        bodyFormData.append('type', type);
        bodyFormData.append('language', language);
        bodyFormData.append('file', file);

        setIsUploadLoading(true);
        uploadManual(bodyFormData);
    };

    const warnDeletion = () => {
        confirm({
            title: intl.formatMessage({ id: 'general.attention' }),
            content: intl.formatMessage({ id: 'general.delete_user_manual_warning' }),
            icon: <ExclamationCircleOutlined/>,
            okText: intl.formatMessage({ id: 'general.yes' }),
            cancelText: intl.formatMessage({ id: 'general.no' }),

            onOk() {
                setIsUploadLoading(true);
                deleteManual({type, language});
            },
        });
    };

    const getLastChangesRef = React.useRef(getManualLastChanges);

    useEffect(() => {
        getLastChangesRef.current = getManualLastChanges;
    }, [getManualLastChanges]);

    useEffect(() => {
        setIsUploadLoading(true);
        getLastChangesRef.current({ type, language });
    }, [type, language, setIsUploadLoading]);


    return (
        <div className='user-manual-upload flex flex-column gap-10'>
            <UserManualLastChanges manualLastChanges={manualLastChanges?.data ?? []} />

            <ComponentAccessCheck permission={'create_student_manual'}>
                 <Select
                     name='language'
                     label={intl.formatMessage({id: 'general.language'})}
                     manualOptions={languageList}
                     value={language}
                     customLayout={{}}
                     onChange={(el: string) => setLanguage(el)}
                     style={{maxWidth: 419}}
                 />
            </ComponentAccessCheck>

            <ComponentAccessCheck permission={'create_student_manual'}>
                <Dragger
                    customRequest={({file}) => handleUpload(file)}
                    accept={'.pdf'}
                    fileList={[]}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text"><FormattedMessage id='general.dragger_text' /></p>
                    <p className="ant-upload-hint"><FormattedMessage id='general.max_upload_file_size' values={{ size: generalHelper.getFileSize(config.maxUploadFileSize, 0) }} /></p>
                    <p className="ant-upload-hint"><FormattedMessage id='general.allowed_file_extensions' values={{ extensions: 'pdf' }} /></p>
                </Dragger>
            </ComponentAccessCheck>

            <ComponentAccessCheck permission={'delete_student_manual'}>
                <Button
                    className={'delete-manual-btn'}
                    danger
                    onClick={warnDeletion}
                    disabled={!isUploaded}
                >
                    <FormattedMessage id='general.delete_user_manual' />
                </Button>
            </ComponentAccessCheck>
        </div>
    );
};

export default UserManualUpload;
